import type { RespuestaDelApi } from '../tipos';
// Espejo local del borrador: reconstruye el CONTENIDO de la pantalla tras recargar.
//
// ⚠ ESTA CABECERA DECIA QUE LA API "NO EXPONE NINGUN GET". YA NO ES CIERTO, y esa
// frase caducada es la que dejo vivo el defecto del 26-ago-2026: `GET /casos/{id}`
// existe desde entonces —`rutas.py`, "Ver el caso guardado (HU-01, retomar un
// borrador)"— y `api/casos.verCaso` estaba escrito y SIN USAR POR NADIE. La
// rehidratacion seguia sacando `version_bloqueo` de aqui, que es justo el dato que
// este espejo no puede conocer.
//
// Reparto de autoridad, para que no haya duda de quien manda:
//   · El SERVIDOR es la autoridad, y ahora se le puede preguntar. `version_bloqueo`
//     sale del `GET` al rehidratar y de cada respuesta despues. Nunca de aqui.
//   · Este espejo aporta el CONTENIDO que el `GET` no devuelve —la seccion 7 es dato
//     L2 y esa ruta no la sirve— y sirve de respaldo si el servidor no responde.
//     Nunca decide, nunca se sincroniza al reves, y se borra al enviar el caso.
//
// `versionBloqueo` sigue guardandose como red de seguridad para el caso sin red, y
// se corrige con `actualizarVersionDelEspejo` en cuanto el servidor da una nueva.

const PREFIJO = 'fv_borrador_';
// Version del formato. Si cambia la forma del caso en pantalla, subirla descarta
// los espejos viejos en vez de intentar leerlos y romper el formulario con datos
// a medio migrar.
// Sube a 2 porque las filas repetibles ahora llevan un `id` local que las de la
// version 1 no tienen: reutilizar un espejo viejo dejaria las `key` en undefined.
const VERSION_DEL_FORMATO = 2;

const clave = (casoId: string): string => `${PREFIJO}${casoId}`;

export function guardarEspejo(casoId: string, caso: unknown, versionBloqueo: number) {
  if (!casoId) return;
  try {
    localStorage.setItem(
      clave(casoId),
      JSON.stringify({ formato: VERSION_DEL_FORMATO, versionBloqueo, caso }),
    );
  } catch {
    // Cuota llena o almacenamiento deshabilitado. No se propaga: perder el espejo
    // degrada la comodidad, no la correccion — el caso ya esta en el servidor.
  }
}

/**
 * Corrige SOLO la version del espejo, dejando el contenido intacto.
 *
 * Hace falta porque `version_bloqueo` la mueve el servidor por caminos que no pasan
 * por un guardado del formulario: asociar o quitar una evidencia (HU-07) hace un
 * `UPDATE` sobre `caso` que el trigger `tg_caso_version_bloqueo` incrementa. Sin
 * esto, el espejo se queda con la version anterior y el siguiente guardado —o la
 * siguiente recarga— recibe un 409 por un conflicto que no existe: adjuntar dos
 * archivos bastaba para dejar el borrador inservible (26-ago-2026).
 *
 * No se reutiliza `guardarEspejo` porque quien conoce la version nueva —el flujo de
 * adjuntos— no tiene delante el caso completo del formulario, y pasarle uno a medias
 * pisaria el contenido bueno con uno viejo.
 */
export function actualizarVersionDelEspejo(casoId: string, versionBloqueo: number) {
  if (!casoId || typeof versionBloqueo !== 'number') return;
  const guardado = leerEspejo(casoId);
  if (!guardado) return; // Sin espejo no hay nada que corregir: no se inventa uno vacio.
  try {
    localStorage.setItem(clave(casoId), JSON.stringify({ ...guardado, versionBloqueo }));
  } catch {
    /* misma razon que en `guardarEspejo`: perder el espejo no rompe la correccion */
  }
}

export function leerEspejo(casoId: string): { caso?: RespuestaDelApi; versionBloqueo?: number } | null {
  if (!casoId) return null;
  try {
    const crudo = localStorage.getItem(clave(casoId));
    if (!crudo) return null;
    const guardado = JSON.parse(crudo);
    if (guardado?.formato !== VERSION_DEL_FORMATO) {
      localStorage.removeItem(clave(casoId));
      return null;
    }
    return guardado;
  } catch {
    return null;
  }
}

/**
 * El contenido con el que se RETOMA un borrador: el que devuelve el servidor, con la
 * seccion 7 del espejo de esta pestana si el `GET` no la sirve.
 *
 * La seccion 7 es dato L2 y `GET /casos/{id}` no la devuelve (RNF-03). Sin conservarla,
 * el formulario la reabriria vacia y el siguiente autoguardado mandaria el bloque en
 * blanco: se perderia en silencio lo que mas costo pedir. No se guarda nada nuevo en el
 * navegador: solo se LEE el espejo que ya existe.
 *
 * Es UNA funcion para los dos caminos de retomar —«Retomar» en la bandeja y recargar la
 * pestana (BUG-370)— porque dos caminos de rehidratacion son dos sitios donde olvidar un
 * campo. Cuando la tarea 338 (HU-32a-C8) retire el dato L2 del espejo, se cambia aqui y en ningun
 * otro sitio.
 */
export function conPersonasDelEspejo<T extends { personas?: unknown }>(casoId: string, caso: T): T {
  if (caso.personas) return caso;
  const personas = leerEspejo(casoId)?.caso?.personas;
  return personas ? { ...caso, personas } : caso;
}

export function olvidarEspejo(casoId: string) {
  if (!casoId) return;
  try {
    localStorage.removeItem(clave(casoId));
  } catch {
    /* nada que hacer */
  }
}

/**
 * El borrador en curso, para poder retomarlo al abrir la aplicacion.
 *
 * Se guarda el id aparte de los espejos porque "cual estaba editando" es una
 * pregunta distinta de "que contenia": al enviar, el caso deja de estar en curso
 * pero su espejo puede seguir sirviendo hasta que se confirme el envio.
 */
const CLAVE_EN_CURSO = 'fv_borrador_en_curso';

export function recordarEnCurso(casoId: string) {
  try {
    localStorage.setItem(CLAVE_EN_CURSO, casoId);
  } catch {
    /* nada que hacer */
  }
}

export function borradorEnCurso() {
  try {
    return localStorage.getItem(CLAVE_EN_CURSO);
  } catch {
    return null;
  }
}

export function limpiarEnCurso() {
  try {
    localStorage.removeItem(CLAVE_EN_CURSO);
  } catch {
    /* nada que hacer */
  }
}

/**
 * EL BORRADOR ABIERTO EN ESTA PESTANA (CP-06-05, 3-sep-2026).
 *
 * Vive en `sessionStorage` y NO en `localStorage`, y esa es toda la idea: sobrevive a
 * recargar la pagina y muere al cerrar la pestana. Es la misma eleccion —y por la misma
 * razon— que hace `sesion.js` con el rol simulado: "sobrevive a recargar, que es lo que
 * hace un registrador que pierde el hilo".
 *
 * QUE PROBLEMA RESUELVE. Al recargar, el registro montaba limpio y nadie sabia que habia
 * un borrador abierto. Seguir escribiendo abria un SEGUNDO caso en el servidor y el
 * primero quedaba abandonado en la bandeja con lo escrito hasta la recarga: un solo hecho
 * partido en dos registros, sin que el registrador lo notara.
 *
 * POR QUE NO SIRVE `borradorEnCurso()`, que ya existe. Ese vive en `localStorage`, o sea
 * que sobrevive a cerrar el navegador. Leerlo al arrancar resucitaria el banner de
 * "tienes un caso a medio registrar" que el operador retiro el 28-ago-2026, cuando
 * decidio que el registro SIEMPRE abre limpio y que retomar tiene un solo camino,
 * Bandeja -> Retomar. Aqui no se ofrece elegir nada: se vuelve donde ya estabas.
 *
 * Abrir la aplicacion en una pestana nueva sigue dando formulario limpio.
 */
const CLAVE_EN_LA_PESTANA = 'fv_borrador_de_la_pestana';

export function recordarEnLaPestana(casoId: string) {
  try {
    sessionStorage.setItem(CLAVE_EN_LA_PESTANA, casoId);
  } catch {
    /* nada que hacer */
  }
}

export function borradorDeLaPestana() {
  try {
    return sessionStorage.getItem(CLAVE_EN_LA_PESTANA);
  } catch {
    return null;
  }
}

export function olvidarBorradorDeLaPestana() {
  try {
    sessionStorage.removeItem(CLAVE_EN_LA_PESTANA);
  } catch {
    /* nada que hacer */
  }
}
